// Real-auth helpers (C4 cutover). The backend GET /auth/me returns the logged-in
// user + their active membership (org_type/tier) + capabilities; the FE maps the
// org_type to the D-7 shell so login lands the user on the right dashboard. In
// maket/simulation mode none of this runs — the mock role-switcher drives the UI
// (non-big-bang cutover; real login coexists).

import { apiUrl } from './api'
import type { AppearancePreference } from './appearance'

// One unmet required agreement for the caller's org, as surfaced on /auth/me
// (mirrors the backend RequiredAgreementItem). Present ⇒ the T&C gate engages;
// `can_current_user_accept` splits SIGNING (owner/authorized) vs WAITING.
export type RequiredAgreement = {
  agreement_id: string
  agreement_version_id: string
  agreement_type: string
  title: string
  status: string
  can_current_user_accept: boolean
}

// The optional ETK (Ticari İleti) opt-in text surfaced inside a KVKK consent.
export type MarketingConsentRef = {
  agreement_id: string
  agreement_version_id: string
  agreement_type: string
  title: string
}

// One personal (per-user) consent the caller still owes — KVKK Aydınlatma, with
// an optional ETK opt-in reference. Independent of org-level required_agreements.
export type PendingConsent = {
  agreement_id: string
  agreement_version_id: string
  agreement_type: string
  title: string
  marketing?: MarketingConsentRef | null
}

export type MeResponse = {
  user: {
    id: string
    email: string
    display_name: string
    phone?: string | null
    // Required contact fields present (display_name + phone). Drives the
    // blocking profile-completion gate.
    profile_complete?: boolean
    status: string
    is_active: boolean
    is_verified: boolean
    token_version: number
    locale?: string
    appearance_preference?: AppearancePreference
  }
  active_membership: {
    id: string
    org_id: string
    scope_family_id: string
    org_type: string
    tier: string
    persona_label: string | null
    required_agreements?: RequiredAgreement[]
    // Seller Staff "hide prices" mode: the server strips money amounts.
    prices_hidden?: boolean
  } | null
  capabilities: string[]
  pending_consents?: PendingConsent[]
}

export type Shell = '/admin' | '/seller' | '/supply'

// D-7 surface segregation: A1/A2 → admin, BO* → seller, SO* → supply.
// An unknown / missing org_type resolves to null — it must NEVER be guessed as
// admin. On the app origin '/admin' is a cross-origin route, so guessing would
// bounce a seller to admin.pexify.net whenever /me hiccups. Callers surface the
// unresolved state instead of navigating.
export function shellForOrgType(orgType: string | null | undefined): Shell | null {
  if (!orgType) return null
  if (orgType.startsWith('BO')) return '/seller'
  if (orgType.startsWith('SO')) return '/supply'
  if (orgType.startsWith('A')) return '/admin'
  return null
}

// The admin surface (admin.pexify.net) is A1/A2 ONLY. Used to gate the admin
// origin so a seller/supply account can never establish an admin-origin session
// (null / non-A org_type is NOT admin — the fallback in shellForOrgType must not
// be read as "is admin").
export function isAdminOrgType(orgType: string | null | undefined): boolean {
  return Boolean(orgType && orgType.startsWith('A'))
}

export async function fetchMe(): Promise<MeResponse | null> {
  try {
    // installApiAuth() attaches the bearer to API-base URLs in real mode.
    const res = await fetch(apiUrl('/auth/me'))
    if (!res.ok) return null
    return (await res.json()) as MeResponse
  } catch {
    return null
  }
}
